import { openUsageLimitModal } from '@eureka/shared/ip-nav';
import { accountDeferred } from '@eureka/utils/account';
import { getIsOversea } from '@eureka/utils/environment';
import { getCurrentLanguage } from '@eureka/language-tool';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { TrackService } from '@/domains/shared/services/track-service';
import { getAiccTaskIdFromCurrentUrl } from '@/presentation/utils/aicc-event-tracking';
import { useUserInfo } from '@/presentation/composables/use-user-info';

const CREDIT_BALANCE_INSUFFICIENT_ERROR_CODE_FRAGMENTS = [
    '7100002'
];
const CREDIT_BALANCE_INSUFFICIENT_ERROR_CODES = [
    'errors.com.patsnap.credits.credit_balance_insufficient'
];
const CREDIT_ERROR_MESSAGE_KEYS = [
    {
        fragment: '7000001',
        messageKey: 'errors.creditRequestTemporarilyUnavailable',
    },
    {
        fragment: '7000002',
        messageKey: 'errors.creditRequestTemporarilyUnavailable',
    },
    {
        fragment: '7000003',
        messageKey: 'errors.creditInvalidParameter',
    },
    {
        fragment: '7100001',
        messageKey: 'errors.creditRecordNotFound',
    },
    {
        fragment: '7100003',
        messageKey: 'errors.creditExpired',
    },
    {
        fragment: '7100004',
        messageKey: 'errors.creditRecordAlreadyExists',
    },
    {
        fragment: '7100005',
        messageKey: 'errors.frozenCreditsInsufficient',
    },
    {
        fragment: '7200001',
        messageKey: 'errors.creditConsumptionRecordNotFound',
    },
    {
        fragment: '7200002',
        messageKey: 'errors.creditConsumptionRecordStatusInvalid',
    },
    {
        fragment: '7200003',
        messageKey: 'errors.creditConsumptionRecordAlreadyProcessed',
    },
    {
        fragment: '7300001',
        messageKey: 'errors.creditAccessForbidden',
    },
    {
        fragment: '7400001',
        messageKey: 'errors.creditTransactionFailed',
    },
    {
        fragment: '7400002',
        messageKey: 'errors.creditDuplicateTransaction',
    }
];
const ENTERPRISE_CREDIT_ERROR_MESSAGE_KEYS = [
    {
        fragment: '7100002',
        messageKey: 'errors.enterpriseCreditsDepleted',
    },
    {
        fragment: '7200004',
        messageKey: 'errors.enterprisePersonalDailyUsageLimitExceeded',
    },
    {
        fragment: '7200005',
        messageKey: 'errors.enterpriseDailyUsageLimitExceeded',
    }
];
const ENTERPRISE_CREDIT_DAILY_LIMIT_ERROR_CODE_FRAGMENTS = ENTERPRISE_CREDIT_ERROR_MESSAGE_KEYS
    .filter(item => item.fragment !== '7100002')
    .map(item => item.fragment);
const CREDIT_ERROR_CODE_FRAGMENTS = CREDIT_ERROR_MESSAGE_KEYS.map(item => item.fragment);
const ENTERPRISE_AVAILABLE_QUOTA_INSUFFICIENT_CONTACT_SALES_MESSAGE_KEY = 'errors.enterpriseAvailableQuotaInsufficientContactSales';
const ENTERPRISE_AVAILABLE_QUOTA_INSUFFICIENT_CONTACT_ADMIN_MESSAGE_KEY = 'errors.enterpriseAvailableQuotaInsufficientContactAdmin';
const PERSONAL_FREE_QUOTA_INSUFFICIENT_CONTACT_SALES_MESSAGE_KEY = 'errors.personalFreeQuotaInsufficientContactSales';
const USAGE_LIMIT_MODAL_DESCRIPTIONS = {
    overseaFree: {
        cn: '你的免费额度不足，请升级至 Pro 继续分析。',
        en: 'Your free quota is insufficient. Upgrade to Pro to continue your analysis.',
        jp: '無料利用枠が不足しています。分析を続けるには Pro にアップグレードしてください。',
        tw: '你的免費額度不足，請升級至 Pro 以繼續分析。',
    },
    overseaPro: {
        cn: '你的可用额度不足，请升级套餐或联系销售获取更多额度。',
        en: 'Your available usage quota is insufficient. Please upgrade your plan or contact sales to get more access.',
        jp: 'ご利用可能な枠が不足しています。プランをアップグレードするか、営業担当者にお問い合わせのうえ、利用枠を追加してください。',
        tw: '你的可用額度不足，請升級方案或聯絡銷售取得更多額度。',
    },
};

function collectBackendErrorCodes(error: unknown) {
    const codes = new Set<string>();

    function walk(value: unknown, depth = 0) {
        if (!value || typeof value !== 'object' || depth > 3) return;

        const record = value as Record<string, unknown>;
        const code = record.error_code;
        if (typeof code === 'string' || typeof code === 'number') {
            codes.add(String(code));
        }
        const numericCode = record.numeric_error_code;
        if (typeof numericCode === 'string' || typeof numericCode === 'number') {
            codes.add(String(numericCode));
        }

        ['data', 'cause', 'response'].forEach(key => walk(record[key], depth + 1));
    }

    walk(error);
    return codes;
}

function isCreditBalanceInsufficientError(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    return Array.from(codes).some(code => {
        return CREDIT_BALANCE_INSUFFICIENT_ERROR_CODES.includes(code) ||
            CREDIT_BALANCE_INSUFFICIENT_ERROR_CODE_FRAGMENTS.some(fragment => code.includes(fragment));
    });
}

function getCreditErrorMessageKey(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    const matchedError = Array.from(codes).map(code => {
        return ENTERPRISE_CREDIT_ERROR_MESSAGE_KEYS.find(item => code.includes(item.fragment));
    }).find(Boolean);

    return matchedError?.messageKey;
}

function getCommonCreditErrorMessageKey(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    const matchedError = Array.from(codes).map(code => {
        return CREDIT_ERROR_MESSAGE_KEYS.find(item => code.includes(item.fragment));
    }).find(Boolean);

    return matchedError?.messageKey;
}

function isCommonCreditErrorCandidate(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    return Array.from(codes).some(code => {
        return CREDIT_ERROR_CODE_FRAGMENTS.some(fragment => code.includes(fragment));
    });
}

function isEnterpriseCreditDailyLimitErrorCandidate(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    return Array.from(codes).some(code => {
        return ENTERPRISE_CREDIT_DAILY_LIMIT_ERROR_CODE_FRAGMENTS.some(fragment => code.includes(fragment));
    });
}

function getUsageLimitModalDescription(type: keyof typeof USAGE_LIMIT_MODAL_DESCRIPTIONS) {
    const lang = getCurrentLanguage();
    const descriptions = USAGE_LIMIT_MODAL_DESCRIPTIONS[type];
    if (lang === 'cn' || lang === 'en' || lang === 'jp' || lang === 'tw') {
        return descriptions[lang];
    }
    return descriptions.en;
}

function getCurrentAiccPageUrl() {
    const hashPath = window.location.hash.replace(/^#/, '').split('?')[0];
    return hashPath || window.location.pathname;
}

function getUsageLimitUpgradeContext() {
    return {
        product_line: 'eureka_telecom',
        trigger_source: 'paywall',
        agent_name: 'telecom_aicc',
        page_url: getCurrentAiccPageUrl(),
        header_type: 'credits_limit_upgrade',
        taskId: getAiccTaskIdFromCurrentUrl(),
    };
}

function buildUsageLimitUpgradeUrl(url: string) {
    const upgradeUrl = new URL(url, window.location.origin);
    const upgradeContext = getUsageLimitUpgradeContext();

    upgradeUrl.searchParams.set('product_line', upgradeContext.product_line);
    upgradeUrl.searchParams.set('trigger_source', upgradeContext.trigger_source);
    upgradeUrl.searchParams.set('agent_name', upgradeContext.agent_name);
    upgradeUrl.searchParams.set('page_url', upgradeContext.page_url);
    upgradeUrl.searchParams.set('header_type', upgradeContext.header_type);

    return upgradeUrl.href;
}

function trackUsageLimitModalUpgradeAndJump(url: string) {
    const targetUrl = buildUsageLimitUpgradeUrl(url);
    const upgradeContext = getUsageLimitUpgradeContext();

    try {
        const trackService = injectService<TrackService>(TrackService);

        trackService.trackAir({
            event_type: 'SS Eureka Upgrade Page',
            source_type: 'upgrade_trigger',
            other_param: 'aicc',
            event_detail_str: JSON.stringify(upgradeContext),
            module_type: 'EUREKA',
        }).finally(() => {
            window.location.href = targetUrl;
        });
    } catch (error) {
        console.warn('[UsageLimitModal] Failed to track upgrade click:', error);
        window.location.href = targetUrl;
    }
}

function openUsageLimitModalWithTracking(description: string) {
    openUsageLimitModal({
        description,
        list: [],
        customUpgradeJump: true,
        onUpgrade: trackUsageLimitModalUpgradeAndJump,
    });
}

async function isEnterpriseAccount() {
    const account = await accountDeferred.promise;
    return !!account?._info?.company_id;
}

async function maybeOpenUsageLimitModalForPersonalCredits(error: unknown) {
    if (!isCreditBalanceInsufficientError(error)) {
        return false;
    }

    try {
        const {
            isPersonalTenant,
            isFreeUser,
            isPro,
            syncUserInfo,
        } = useUserInfo();

        await syncUserInfo();

        if (!isPersonalTenant.value) {
            console.debug('[UsageLimitModal] Skip insufficient credits modal for enterprise user', {
                codes: Array.from(collectBackendErrorCodes(error)),
            });
            return false;
        }

        if (getIsOversea()) {
            if (isFreeUser.value) {
                openUsageLimitModalWithTracking(getUsageLimitModalDescription('overseaFree'));
            } else if (isPro.value) {
                openUsageLimitModalWithTracking(getUsageLimitModalDescription('overseaPro'));
            } else {
                return false;
            }
        } else {
            console.debug('[UsageLimitModal] Skip insufficient credits modal for CN personal user', {
                codes: Array.from(collectBackendErrorCodes(error)),
            });
            return false;
        }

        console.debug('[UsageLimitModal] Opened for personal credit balance insufficient:', {
            isOversea: getIsOversea(),
            isFreeUser: isFreeUser.value,
            isPro: isPro.value,
            codes: Array.from(collectBackendErrorCodes(error)),
        });
        return true;
    } catch (accountError) {
        console.warn('[UsageLimitModal] Failed to read account before opening modal:', accountError);
        return false;
    }
}

async function getEnterpriseCreditErrorMessageKey(error: unknown) {
    const messageKey = getCreditErrorMessageKey(error);
    if (!messageKey) {
        return undefined;
    }

    try {
        if (!isCreditBalanceInsufficientError(error)) {
            return await isEnterpriseAccount() ? messageKey : undefined;
        }

        const {
            isEnterpriseTenant,
            hasEnterpriseFreePackage,
            hasEnterpriseTrialPackage,
            hasEnterpriseCommercialPackage,
            syncUserInfo,
        } = useUserInfo();

        await syncUserInfo();

        if (!isEnterpriseTenant.value) {
            return undefined;
        }

        if (hasEnterpriseCommercialPackage.value) {
            return ENTERPRISE_AVAILABLE_QUOTA_INSUFFICIENT_CONTACT_ADMIN_MESSAGE_KEY;
        }

        if (hasEnterpriseFreePackage.value || hasEnterpriseTrialPackage.value) {
            return ENTERPRISE_AVAILABLE_QUOTA_INSUFFICIENT_CONTACT_SALES_MESSAGE_KEY;
        }

        return messageKey;
    } catch (accountError) {
        console.warn('[UsageLimitModal] Failed to read account before checking enterprise credit message:', accountError);
        return undefined;
    }
}

async function getCreditBalanceInsufficientToastMessageKey(error: unknown) {
    if (!isCreditBalanceInsufficientError(error)) {
        return undefined;
    }

    try {
        const {
            isPersonalTenant,
            isFreeUser,
            syncUserInfo,
        } = useUserInfo();

        await syncUserInfo();

        if (!getIsOversea() && isPersonalTenant.value && isFreeUser.value) {
            return PERSONAL_FREE_QUOTA_INSUFFICIENT_CONTACT_SALES_MESSAGE_KEY;
        }

        return getEnterpriseCreditErrorMessageKey(error);
    } catch (accountError) {
        console.warn('[UsageLimitModal] Failed to read account before checking credit balance message:', accountError);
        return undefined;
    }
}

export {
    maybeOpenUsageLimitModalForPersonalCredits,
    getCommonCreditErrorMessageKey,
    getEnterpriseCreditErrorMessageKey,
    getCreditBalanceInsufficientToastMessageKey,
    isCommonCreditErrorCandidate,
    isEnterpriseCreditDailyLimitErrorCandidate,
    isCreditBalanceInsufficientError,
};
